import { useEffect, useState } from "react";
import type { Newsletter } from "../../api/types";
import { t } from "../../i18n";

interface Props {
  token: string;
}

/**
 * "Uitschrijven" in the mail about the Dagpromo (Furkan, 14/10/2026): the customer's page behind that link,
 * on any phone, without signing in. Opening it changes nothing (a mail app may look at a link before its
 * reader does): the customer stops the shop's actions by mail with the button, and takes them again the
 * same way.
 */
export function NewsletterPage({ token }: Props) {
  const text = t.newsletter;
  const [choice, setChoice] = useState<Newsletter | null>(null);
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const address = `/api/v1/newsletter/${encodeURIComponent(token)}`;

  useEffect(() => {
    let stopped = false;
    fetch(address)
      .then(async (response) => {
        if (stopped) return;
        if (response.ok) setChoice((await response.json()) as Newsletter);
        else setGone(true);
      })
      .catch(() => {
        if (!stopped) setFailed(true);
      });
    return () => {
      stopped = true;
    };
  }, [address]);

  useEffect(() => {
    document.title = choice ? `${text.title} · ${choice.shop}` : text.title;
  }, [choice, text.title]);

  const save = (subscribed: boolean) => {
    setBusy(true);
    setFailed(false);
    fetch(address, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ subscribed }),
    })
      .then(async (response) => {
        if (response.ok) setChoice((await response.json()) as Newsletter);
        else setFailed(true);
      })
      .catch(() => {
        setFailed(true);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (gone) {
    return (
      <main className="trackpage">
        <p className="trackgone">{text.gone}</p>
      </main>
    );
  }
  if (!choice) {
    return (
      <main className="trackpage" aria-busy={!failed}>
        {failed && <p className="trackgone">{text.failed}</p>}
      </main>
    );
  }

  return (
    <main className="trackpage">
      <h1>{text.title}</h1>
      <div className="newsletter">
        <b>{choice.shop}</b>
        <p role="status">{choice.subscribed ? text.subscribed : text.unsubscribed}</p>
        <button
          type="button"
          className={choice.subscribed ? "btn primary" : "btn"}
          disabled={busy}
          onClick={() => {
            save(!choice.subscribed);
          }}
        >
          {choice.subscribed ? text.stop : text.resume}
        </button>
        {failed && <p className="pinmsg">{text.failed}</p>}
      </div>
    </main>
  );
}

/** The token when this page is the one behind the link in the mail (/nieuwsbrief/<token>), else null. */
export function newsletterToken(pathname: string): string | null {
  return /^\/nieuwsbrief\/([A-Za-z0-9]{24})\/?$/.exec(pathname)?.[1] ?? null;
}
